import { createContext, useCallback, useContext } from 'react';

import { notification } from 'antd';
import {
  CheckCircle,
  ExclamationCircle,
  ExclamationTriangle,
  InfoCircle,
} from 'react-bootstrap-icons';
import { useMediaQuery } from 'react-responsive';
import {
  NotificationOptionalParams,
  NotificationProviderProps,
  NotificationTypeEnum,
} from '../types/notificationContextTypes';
import RESOURCE_CONFIGS from '../constants/resourceConfig';

const NotificationContext = createContext({
  successNotification: (_message: string, _params?: NotificationOptionalParams) => {},
  errorNotification: (_message: string, _params?: NotificationOptionalParams) => {},
  infoNotification: (_message: string, _params?: NotificationOptionalParams) => {},
  warningNotification: (_message: string, _params?: NotificationOptionalParams) => {},
});

export const NotificationContextProvider = ({ children }: NotificationProviderProps) => {
  const [api, contextHolder] = notification.useNotification();
  const isTabletOrMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.MD})`,
  });
  const iconSize = isTabletOrMobile ? 16 : 22;
  const mobileStyleClass =
    '!px-4 !py-3 [&_.ant-notification-notice-message]:!ml-6 [&_.ant-notification-notice-message]:!pr-7 [&_.ant-notification-notice-close]:!top-[17px] [&_.ant-notification-notice-icon]:top-[19px]';

  const pickNotificationTypeOptions = (type: NotificationTypeEnum, message: string) => {
    if (type === NotificationTypeEnum.ERROR) {
      return {
        className: `error-notifications bg-primary-red bg-opacity-25 rounded-md ${isTabletOrMobile && mobileStyleClass}`,
        icon: <ExclamationCircle size={iconSize} className="text-primary-red" />,
        messageContent: (
          <span className="text-sm text-primary-red md:text-base">{`${message}`}</span>
        ),
      };
    } else if (type === NotificationTypeEnum.SUCCESS) {
      return {
        className: `success-notifications bg-primary-green bg-opacity-25 rounded-md ${isTabletOrMobile && mobileStyleClass}`,
        icon: <CheckCircle size={iconSize} className="text-primary-green" />,
        messageContent: (
          <span className="text-sm text-primary-green md:text-base">{`${message}`}</span>
        ),
      };
    } else if (type === NotificationTypeEnum.INFO) {
      return {
        className: `info-notifications ${isTabletOrMobile && mobileStyleClass}`,
        icon: <InfoCircle size={iconSize} className="text-status-pending-text" />,
        messageContent: (
          <span className="text-sm text-primary-red md:text-base">{`${message}`}</span>
        ),
      };
    } else if (type === NotificationTypeEnum.WARNING) {
      return {
        className: `warning-notifications bg-tag-orange bg-opacity-25 ${isTabletOrMobile && mobileStyleClass}`,
        icon: <ExclamationTriangle size={iconSize} className="text-tag-orange mt-0" />,
        messageContent: (
          <span className="text-sm text-tag-orange md:text-base">{`${message}`}</span>
        ),
      };
    }

    return {
      className: `default-notifications ${isTabletOrMobile && mobileStyleClass}`,
      icon: <CheckCircle size={iconSize} className="text-status-success-text" />,
      messageContent: (
        <span className="text-status-success-text text-sm md:text-base">{`${message}`}</span>
      ),
    };
  };

  const openNotification = useCallback(
    (type: NotificationTypeEnum, message: string, params?: NotificationOptionalParams) => {
      const { icon, messageContent, className } = pickNotificationTypeOptions(type, message);

      api[type]({
        message: messageContent,
        duration: params?.duration || 5,
        icon: icon,
        className,
        onClose: params?.onClose || null,
        style: {},
      });
    },
    [api]
  );

  return (
    <NotificationContext.Provider
      value={{
        successNotification: (message: string, params?: NotificationOptionalParams) =>
          openNotification(NotificationTypeEnum.SUCCESS, message, params),
        errorNotification: (message: string, params?: NotificationOptionalParams) =>
          openNotification(NotificationTypeEnum.ERROR, message, params),
        infoNotification: (message: string, params?: NotificationOptionalParams) =>
          openNotification(NotificationTypeEnum.INFO, message, params),
        warningNotification: (message: string, params?: NotificationOptionalParams) =>
          openNotification(NotificationTypeEnum.WARNING, message, params),
      }}
    >
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  return useContext(NotificationContext);
};
